import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Line, OrbitControls, Sphere, Text } from "@react-three/drei";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import { Mesh, Vector3 } from "three";
import { CircleHelp, Rotate3D } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { MathText } from "@/components/MathText";
import { cn } from "@/lib/utils";
import {
  BUILT_IN_GATES,
  C0,
  C1,
  applyGate,
  blochToState,
  complexToString,
  gateRx,
  gateRy,
  gateRz,
  isUnitary,
  normalizeState,
  parseComplex,
  parseMatrixText,
  stateToBloch,
  type ChangeMeta,
  type Complex,
  type ComplexMatrix2x2,
  type GateDescriptor,
  type QubitState,
} from "@/lib/quantum";

type InteractionMode = "orbit-and-state-drag" | "orbit-only";
type ThemeMode = "auto" | "light" | "dark";

export type BlochSphereProps = {
  value?: QubitState;
  defaultValue?: QubitState;
  onChange?: (next: QubitState, meta: ChangeMeta) => void;
  onGateApplied?: (gate: GateDescriptor, next: QubitState) => void;
  unitaryTolerance?: number;
  theme?: ThemeMode;
  interactionMode?: InteractionMode;
  showHistory?: boolean;
  className?: string;
  ambientPan?: boolean;
  ambientPanSpeed?: number;
  ambientPanPauseOnInteract?: boolean;
};

type HistoryEntry = {
  id: number;
  label: string;
  state: QubitState;
};

function cloneState(state: QubitState): QubitState {
  return {
    alpha: { ...state.alpha },
    beta: { ...state.beta },
  };
}

function formatDegrees(v: number): string {
  return `${Math.round(v)} deg`;
}

function buildMatrixFromGrid(cells: string[][]): { matrix: ComplexMatrix2x2 | null; error?: string } {
  const a = parseComplex(cells[0]?.[0] ?? "");
  const b = parseComplex(cells[0]?.[1] ?? "");
  const cCell = parseComplex(cells[1]?.[0] ?? "");
  const d = parseComplex(cells[1]?.[1] ?? "");

  if (!a || !b || !cCell || !d) {
    return { matrix: null, error: "Use valid complex values in all matrix cells (example: 0.5-0.5i)." };
  }

  return {
    matrix: [
      [a, b],
      [cCell, d],
    ],
  };
}

function blochRadiusClamp(x: number, y: number, z: number): { x: number; y: number; z: number } {
  const m = Math.hypot(x, y, z) || 1;
  return { x: x / m, y: y / m, z: z / m };
}

function Scene({
  bloch,
  onDragVector,
  allowDrag,
  ambientPanActive,
  ambientPanSpeed,
  ambientPanPauseOnInteract,
  onInteractionChange,
  onManualInteraction,
}: {
  bloch: { x: number; y: number; z: number };
  onDragVector: (next: { x: number; y: number; z: number }) => void;
  allowDrag: boolean;
  ambientPanActive: boolean;
  ambientPanSpeed: number;
  ambientPanPauseOnInteract: boolean;
  onInteractionChange: (interacting: boolean) => void;
  onManualInteraction: () => void;
}) {
  const sphereRadius = 2.35;
  const axisExtent = 2.95;
  const palette = {
    x: "#ef476f",
    y: "#06d6a0",
    z: "#4cc9f0",
    arrow: "#2ec27e",
    arrowEmissive: "#145a3f",
    sphere: "#7dd3fc",
  } as const;
  const controlsRef = useRef<OrbitControlsImpl | null>(null);
  const arrowHeadRef = useRef<Mesh | null>(null);
  const [draggingVector, setDraggingVector] = useState(false);
  const [suppressUntil, setSuppressUntil] = useState(0);
  const pointerDown = useRef(false);
  const { camera, gl } = useThree();

  const vector = useMemo(
    () => new Vector3(bloch.x * sphereRadius, bloch.y * sphereRadius, bloch.z * sphereRadius),
    [bloch.x, bloch.y, bloch.z, sphereRadius],
  );
  const tipPos = useMemo(
    () => [bloch.x * sphereRadius, bloch.y * sphereRadius, bloch.z * sphereRadius] as [number, number, number],
    [bloch.x, bloch.y, bloch.z, sphereRadius],
  );

  useEffect(() => {
    const node = gl.domElement;
    const release = () => {
      pointerDown.current = false;
      setDraggingVector(false);
      onInteractionChange(false);
      setSuppressUntil(performance.now() + 2500);
    };

    node.addEventListener("pointerup", release);
    node.addEventListener("pointerleave", release);
    return () => {
      node.removeEventListener("pointerup", release);
      node.removeEventListener("pointerleave", release);
    };
  }, [gl.domElement, onInteractionChange]);

  useEffect(() => {
    const controls = controlsRef.current;
    if (!controls) return;
    const onStart = () => {
      onManualInteraction();
      onInteractionChange(true);
      if (ambientPanPauseOnInteract) setSuppressUntil(performance.now() + 2500);
    };
    const onEnd = () => {
      onInteractionChange(false);
      if (ambientPanPauseOnInteract) setSuppressUntil(performance.now() + 2500);
    };
    controls.addEventListener("start", onStart);
    controls.addEventListener("end", onEnd);
    return () => {
      controls.removeEventListener("start", onStart);
      controls.removeEventListener("end", onEnd);
    };
  }, [ambientPanPauseOnInteract, onInteractionChange, onManualInteraction]);

  useFrame((_state, delta) => {
    const controls = controlsRef.current;
    if (!controls) return;
    const now = performance.now();
    const canAutoRotate = ambientPanActive && (!ambientPanPauseOnInteract || now > suppressUntil) && !draggingVector;

    if (canAutoRotate) {
      const speed = ambientPanSpeed * delta;
      camera.position.applyAxisAngle(new Vector3(0, 1, 0), speed);
      camera.lookAt(0, 0, 0);
      controls.update();
    } else {
      controls.update();
    }

    if (arrowHeadRef.current) {
      const direction = vector.clone().normalize();
      arrowHeadRef.current.position.set(vector.x, vector.y, vector.z);
      arrowHeadRef.current.quaternion.setFromUnitVectors(new Vector3(0, 1, 0), direction);
    }
  });

  return (
    <>
      <ambientLight intensity={0.65} />
      <directionalLight position={[2, 2, 2]} intensity={1.25} />
      <directionalLight position={[-2, -1, -1]} intensity={0.55} />
      <pointLight position={[0, 3, 0]} intensity={0.2} />

      <mesh>
        <sphereGeometry args={[sphereRadius, 64, 64]} />
        <meshPhysicalMaterial
          color={palette.sphere}
          transparent
          opacity={0.24}
          roughness={0.15}
          metalness={0.2}
          clearcoat={0.9}
          clearcoatRoughness={0.08}
          transmission={0.08}
        />
      </mesh>
      <mesh>
        <sphereGeometry args={[sphereRadius + 0.02, 64, 64]} />
        <meshBasicMaterial color={palette.sphere} wireframe transparent opacity={0.2} />
      </mesh>

      <Line points={[[-axisExtent, 0, 0], [axisExtent, 0, 0]]} color={palette.x} lineWidth={3.2} />
      <Line points={[[0, -axisExtent, 0], [0, axisExtent, 0]]} color={palette.y} lineWidth={3.2} />
      <Line points={[[0, 0, -axisExtent], [0, 0, axisExtent]]} color={palette.z} lineWidth={3.2} />

      <Line
        points={Array.from({ length: 80 }, (_, i) => {
          const t = (Math.PI * 2 * i) / 79;
          return [sphereRadius * Math.cos(t), sphereRadius * Math.sin(t), 0] as [number, number, number];
        })}
        color={palette.z}
        lineWidth={1.3}
        opacity={0.35}
        transparent
      />
      <Line
        points={Array.from({ length: 80 }, (_, i) => {
          const t = (Math.PI * 2 * i) / 79;
          return [0, sphereRadius * Math.cos(t), sphereRadius * Math.sin(t)] as [number, number, number];
        })}
        color={palette.z}
        lineWidth={1.3}
        opacity={0.3}
        transparent
      />
      <Line
        points={Array.from({ length: 80 }, (_, i) => {
          const t = (Math.PI * 2 * i) / 79;
          return [sphereRadius * Math.cos(t), 0, sphereRadius * Math.sin(t)] as [number, number, number];
        })}
        color={palette.z}
        lineWidth={1.3}
        opacity={0.3}
        transparent
      />

      <Line
        points={[[0, 0, 0], [bloch.x * sphereRadius * 0.91, bloch.y * sphereRadius * 0.91, bloch.z * sphereRadius * 0.91]]}
        color={palette.arrow}
        lineWidth={4.1}
        depthTest={false}
      />
      <mesh ref={arrowHeadRef}>
        <coneGeometry args={[0.085, 0.22, 28]} />
        <meshStandardMaterial
          color={palette.arrow}
          emissive={palette.arrowEmissive}
          emissiveIntensity={0.4}
          depthTest={false}
          depthWrite={false}
        />
      </mesh>

      <Sphere
        args={[0.04, 16, 16]}
        position={tipPos}
        onPointerDown={event => {
          if (!allowDrag) return;
          event.stopPropagation();
          onManualInteraction();
          pointerDown.current = true;
          setDraggingVector(true);
          onInteractionChange(true);
        }}
      >
        <meshStandardMaterial color={palette.arrow} emissive={palette.arrowEmissive} emissiveIntensity={0.35} />
      </Sphere>

      <mesh
        visible={false}
        onPointerMove={event => {
          if (!allowDrag || !pointerDown.current || !draggingVector) return;
          event.stopPropagation();
          const p = event.point;
          const next = blochRadiusClamp(p.x / sphereRadius, p.y / sphereRadius, p.z / sphereRadius);
          onDragVector(next);
        }}
      >
        <sphereGeometry args={[sphereRadius + 0.03, 40, 40]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>

      <Text position={[axisExtent + 0.18, 0, 0]} color={palette.x} fontSize={0.18}>
        X
      </Text>
      <Text position={[0, axisExtent + 0.14, 0]} color={palette.y} fontSize={0.18}>
        Y
      </Text>
      <Text position={[0, 0, axisExtent + 0.18]} color={palette.z} fontSize={0.18}>
        Z
      </Text>
      <Text position={[0, 0, sphereRadius + 0.22]} color="#f8fafc" fontSize={0.12}>
        {"|0>"}
      </Text>
      <Text position={[0, 0, -sphereRadius - 0.22]} color="#f8fafc" fontSize={0.12}>
        {"|1>"}
      </Text>

      <OrbitControls ref={controlsRef} makeDefault enablePan={false} enableZoom minDistance={1.6} maxDistance={16} />
    </>
  );
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  return reduced;
}

export function BlochSphere({
  value,
  defaultValue = { alpha: C1, beta: C0 },
  onChange,
  onGateApplied,
  unitaryTolerance = 1e-6,
  theme = "auto",
  interactionMode = "orbit-and-state-drag",
  showHistory = true,
  className,
  ambientPan = false,
  ambientPanSpeed = 0.12,
  ambientPanPauseOnInteract = true,
}: BlochSphereProps) {
  const APPROX_NORMALIZATION_TOLERANCE = 1e-2;
  const controlled = value !== undefined;
  const [internalState, setInternalState] = useState<QubitState>(cloneState(defaultValue));
  const [history, setHistory] = useState<HistoryEntry[]>([{ id: 0, label: "Initial |0>", state: cloneState(defaultValue) }]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [angleDeg, setAngleDeg] = useState(90);
  const [interactionActive, setInteractionActive] = useState(false);
  const [matrixGrid, setMatrixGrid] = useState<string[][]>([
    ["1", "0"],
    ["0", "1"],
  ]);
  const [matrixText, setMatrixText] = useState("1,0;0,1");
  const [ambientPanEnabled, setAmbientPanEnabled] = useState(ambientPan);
  const [alphaInput, setAlphaInput] = useState("1");
  const [betaInput, setBetaInput] = useState("0");
  const [stateInputError, setStateInputError] = useState<string | null>(null);
  const reducedMotion = useReducedMotion();

  const state = controlled ? value : internalState;
  const currentState = state ?? defaultValue;
  const bloch = stateToBloch(currentState);
  const allowDrag = interactionMode === "orbit-and-state-drag";

  useEffect(() => {
    setAmbientPanEnabled(ambientPan);
  }, [ambientPan]);

  const effectiveAmbientPan = ambientPanEnabled && !reducedMotion;
  const activeThemeClass = theme === "dark" ? "dark" : theme === "light" ? "light" : "";

  useEffect(() => {
    setAlphaInput(complexToString(currentState.alpha, 6));
    setBetaInput(complexToString(currentState.beta, 6));
  }, [currentState.alpha.im, currentState.alpha.re, currentState.beta.im, currentState.beta.re]);

  const matrixFromGrid = useMemo(() => buildMatrixFromGrid(matrixGrid), [matrixGrid]);
  const parsedAlpha = useMemo(() => parseComplex(alphaInput), [alphaInput]);
  const parsedBeta = useMemo(() => parseComplex(betaInput), [betaInput]);
  const stateInputValidation = useMemo(() => {
    if (!parsedAlpha || !parsedBeta) {
      return { valid: false, message: "Enter valid complex values for a and b." };
    }
    const normSq =
      parsedAlpha.re * parsedAlpha.re +
      parsedAlpha.im * parsedAlpha.im +
      parsedBeta.re * parsedBeta.re +
      parsedBeta.im * parsedBeta.im;
    if (!Number.isFinite(normSq) || normSq <= 1e-12) {
      return { valid: false, message: "a and b cannot both be zero." };
    }
    if (Math.abs(normSq - 1) > APPROX_NORMALIZATION_TOLERANCE) {
      return {
        valid: false,
        message: `State is too far from normalized: |a|^2 + |b|^2 should be close to 1 (current ${normSq.toFixed(4)}).`,
      };
    }
    if (Math.abs(normSq - 1) > 1e-6) {
      return {
        valid: true,
        message: `Approx-normalized input accepted (|a|^2 + |b|^2 = ${normSq.toFixed(4)}). Apply will normalize exactly.`,
      };
    }
    return { valid: true, message: "Valid normalized state." };
  }, [parsedAlpha, parsedBeta, APPROX_NORMALIZATION_TOLERANCE]);
  const gridValidation = useMemo(() => {
    if (!matrixFromGrid.matrix) {
      return { valid: false, error: matrixFromGrid.error ?? "Matrix parsing failed." };
    }
    return isUnitary(matrixFromGrid.matrix, unitaryTolerance);
  }, [matrixFromGrid, unitaryTolerance]);

  const emitChange = useCallback(
    (next: QubitState, meta: ChangeMeta) => {
      if (!controlled) setInternalState(cloneState(next));
      onChange?.(next, meta);
    },
    [controlled, onChange],
  );

  const pushHistory = useCallback((label: string, nextState: QubitState) => {
    setHistory(prev => {
      const truncated = prev.slice(0, historyIndex + 1);
      const nextEntry = { id: Date.now() + Math.floor(Math.random() * 10_000), label, state: cloneState(nextState) };
      const updated = [...truncated, nextEntry];
      setHistoryIndex(updated.length - 1);
      return updated;
    });
  }, [historyIndex]);

  const applyGateDescriptor = useCallback(
    (gate: GateDescriptor) => {
      const next = applyGate(currentState, gate.matrix);
      emitChange(next, { source: "gate", historyIndex: historyIndex + 1, viewMode: effectiveAmbientPan ? "ambient-pan" : "manual" });
      pushHistory(gate.label, next);
      onGateApplied?.(gate, next);
    },
    [currentState, effectiveAmbientPan, emitChange, historyIndex, onGateApplied, pushHistory],
  );

  const onDragVector = useCallback(
    (nextBloch: { x: number; y: number; z: number }) => {
      const nextState = blochToState(nextBloch);
      emitChange(nextState, { source: "drag", viewMode: effectiveAmbientPan ? "ambient-pan" : "manual" });
    },
    [effectiveAmbientPan, emitChange],
  );

  const undo = () => {
    if (historyIndex <= 0) return;
    const nextIndex = historyIndex - 1;
    const entry = history[nextIndex];
    if (!entry) return;
    setHistoryIndex(nextIndex);
    emitChange(entry.state, { source: "external", historyIndex: nextIndex });
  };

  const redo = () => {
    if (historyIndex >= history.length - 1) return;
    const nextIndex = historyIndex + 1;
    const entry = history[nextIndex];
    if (!entry) return;
    setHistoryIndex(nextIndex);
    emitChange(entry.state, { source: "external", historyIndex: nextIndex });
  };

  const reset = () => {
    const next = { alpha: C1, beta: C0 };
    emitChange(next, { source: "reset", historyIndex: 0, viewMode: effectiveAmbientPan ? "ambient-pan" : "manual" });
    setHistory([{ id: 0, label: "Reset |0>", state: cloneState(next) }]);
    setHistoryIndex(0);
  };

  const applyRotation = (axis: "x" | "y" | "z") => {
    const theta = (angleDeg * Math.PI) / 180;
    const matrix = axis === "x" ? gateRx(theta) : axis === "y" ? gateRy(theta) : gateRz(theta);
    applyGateDescriptor({ id: `R${axis}`, label: `R${axis.toUpperCase()}(${formatDegrees(angleDeg)})`, matrix });
  };

  const applyCustomFromGrid = () => {
    if (!gridValidation.valid || !matrixFromGrid.matrix) return;
    const matrix = matrixFromGrid.matrix;
    applyGateDescriptor({ id: "custom", label: "Custom U", matrix });
  };

  const applyCustomFromText = () => {
    const parsed = parseMatrixText(matrixText);
    if (!parsed) return;
    const validation = isUnitary(parsed, unitaryTolerance);
    if (!validation.valid) return;
    applyGateDescriptor({ id: "custom", label: "Custom U", matrix: parsed });
    setMatrixGrid([
      [complexToString(parsed[0][0], 3), complexToString(parsed[0][1], 3)],
      [complexToString(parsed[1][0], 3), complexToString(parsed[1][1], 3)],
    ]);
  };

  const parseTextIntoGrid = () => {
    const parsed = parseMatrixText(matrixText);
    if (!parsed) return;
    setMatrixGrid([
      [complexToString(parsed[0][0], 6), complexToString(parsed[0][1], 6)],
      [complexToString(parsed[1][0], 6), complexToString(parsed[1][1], 6)],
    ]);
  };

  const applyExactState = () => {
    if (!parsedAlpha || !parsedBeta || !stateInputValidation.valid) {
      setStateInputError(stateInputValidation.message);
      return;
    }
    const next = normalizeState({ alpha: parsedAlpha, beta: parsedBeta });
    setStateInputError(null);
    emitChange(next, { source: "external", historyIndex: historyIndex + 1 });
    pushHistory("Set a|0> + b|1>", next);
  };

  return (
    <Card className={cn("overflow-hidden border-border/70 shadow-lg", activeThemeClass, className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-xl tracking-tight">Interactive Bloch Sphere</CardTitle>
        <CardDescription>
          Drag to orbit. {allowDrag ? "Drag the green tip to move the qubit state." : "State dragging is disabled."}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 p-3 lg:grid-cols-[minmax(0,1fr)_420px]">
        <div
          className={cn(
            "relative h-[59vh] w-full rounded-xl border border-border/60 md:h-[66vh]",
            "bg-[radial-gradient(circle,rgba(148,163,184,0.35)_1px,transparent_1.4px)] bg-[length:18px_18px]",
            "bg-slate-50/60 dark:bg-slate-900/25",
          )}
        >
          <div className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-br from-cyan-300/10 via-transparent to-emerald-300/10" />
          <Canvas className="h-full w-full" camera={{ position: [4.8, 4.05, 4.8], fov: 30 }}>
            <Scene
              bloch={bloch}
              onDragVector={onDragVector}
              allowDrag={allowDrag}
              ambientPanActive={effectiveAmbientPan}
              ambientPanSpeed={ambientPanSpeed}
              ambientPanPauseOnInteract={ambientPanPauseOnInteract}
              onInteractionChange={setInteractionActive}
              onManualInteraction={() => {
                if (ambientPanEnabled) setAmbientPanEnabled(false);
              }}
            />
          </Canvas>
          <div className="absolute left-3 top-3 rounded-md border bg-background/85 px-2 py-1 text-xs backdrop-blur">
            View: {effectiveAmbientPan ? "Ambient pan" : "Manual"} {interactionActive ? "(interacting)" : ""}
          </div>
        </div>
        <aside className="h-[59vh] overflow-auto rounded-xl border border-border/80 bg-background/96 p-3 shadow-xl md:h-[66vh]">
          <div className="space-y-4">
            <section className="space-y-2 rounded-lg border border-border/70 bg-muted/35 p-3">
              <h3 className="text-sm font-semibold tracking-wide text-muted-foreground">View + State</h3>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={effectiveAmbientPan ? "default" : "outline"}
                  size="sm"
                  onClick={() => setAmbientPanEnabled(current => !current)}
                >
                  <Rotate3D className="size-4" />
                  Ambient pan
                </Button>
                <Button variant="outline" size="sm" onClick={reset}>
                  {"Reset |0>"}
                </Button>
                <Button variant="outline" size="sm" onClick={undo} disabled={historyIndex <= 0}>
                  Undo
                </Button>
                <Button variant="outline" size="sm" onClick={redo} disabled={historyIndex >= history.length - 1}>
                  Redo
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                alpha = {complexToString(currentState.alpha)} | beta = {complexToString(currentState.beta)}
              </p>
              <p className="text-xs text-muted-foreground">
                x={bloch.x.toFixed(3)} y={bloch.y.toFixed(3)} z={bloch.z.toFixed(3)}
              </p>
            </section>

            <section className="space-y-2 rounded-lg border border-border/70 bg-muted/30 p-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold tracking-wide text-muted-foreground">Set Exact State</h3>
                <div className="group relative">
                  <CircleHelp className="size-4 text-muted-foreground/80" />
                  <div className="pointer-events-none absolute right-0 top-5 z-30 w-72 rounded-md border border-border bg-background/95 p-2 text-xs opacity-0 shadow-xl transition group-hover:opacity-100">
                    <MathText latex={"\\left|\\psi\\right\\rangle = a\\left|0\\right\\rangle + b\\left|1\\right\\rangle"} displayMode />
                    <MathText latex={"|a|^2 + |b|^2 = 1"} className="mt-1 block" />
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Complex form example: 0.5-0.5i</p>
              <div className="grid grid-cols-[20px_1fr] items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">a</span>
                <Input value={alphaInput} onChange={event => setAlphaInput(event.target.value)} placeholder="1" />
                <span className="text-xs font-medium text-muted-foreground">b</span>
                <Input value={betaInput} onChange={event => setBetaInput(event.target.value)} placeholder="0" />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={applyExactState} disabled={!stateInputValidation.valid}>
                  Apply a,b
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setAlphaInput(complexToString(currentState.alpha, 6));
                    setBetaInput(complexToString(currentState.beta, 6));
                    setStateInputError(null);
                  }}
                >
                  Sync from current
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (!parsedAlpha || !parsedBeta) return;
                    const normalized = normalizeState({ alpha: parsedAlpha, beta: parsedBeta });
                    setAlphaInput(complexToString(normalized.alpha, 6));
                    setBetaInput(complexToString(normalized.beta, 6));
                    setStateInputError(null);
                  }}
                  disabled={!parsedAlpha || !parsedBeta}
                >
                  Normalize
                </Button>
              </div>
              <MathText
                latex={`a=${currentState.alpha.re.toFixed(3)}${currentState.alpha.im >= 0 ? "+" : ""}${currentState.alpha.im.toFixed(3)}i,\\quad b=${currentState.beta.re.toFixed(3)}${currentState.beta.im >= 0 ? "+" : ""}${currentState.beta.im.toFixed(3)}i`}
                className="block text-xs text-muted-foreground"
              />
              <p
                className={cn(
                  "text-xs",
                  stateInputValidation.valid ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400",
                )}
              >
                {stateInputError ?? stateInputValidation.message}
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold tracking-wide text-muted-foreground">Common Gates</h3>
              <div className="grid grid-cols-4 gap-2">
                {BUILT_IN_GATES.map(gate => (
                  <Button key={gate.id} variant="secondary" size="sm" onClick={() => applyGateDescriptor(gate)}>
                    {gate.label}
                  </Button>
                ))}
              </div>
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold tracking-wide text-muted-foreground">Parameterized Rotations</h3>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={angleDeg}
                  min={-720}
                  max={720}
                  step={1}
                  onChange={event => setAngleDeg(Number(event.target.value))}
                />
                <Button variant="outline" size="sm" onClick={() => applyRotation("x")}>
                  Rx
                </Button>
                <Button variant="outline" size="sm" onClick={() => applyRotation("y")}>
                  Ry
                </Button>
                <Button variant="outline" size="sm" onClick={() => applyRotation("z")}>
                  Rz
                </Button>
              </div>
            </section>

            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold tracking-wide text-muted-foreground">Custom 2x2 Matrix</h3>
                <div className="group relative">
                  <CircleHelp className="size-4 text-muted-foreground/80" />
                  <div className="pointer-events-none absolute right-0 top-5 z-30 w-72 rounded-md border border-border bg-background/95 p-2 text-xs opacity-0 shadow-xl transition group-hover:opacity-100">
                    <MathText latex={"U^{\\dagger}U = I"} displayMode />
                    <p className="mt-1 text-xs text-muted-foreground">Only unitary matrices can be applied.</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {matrixGrid.map((row, rowIndex) =>
                  row.map((cell, colIndex) => (
                    <Input
                      key={`${rowIndex}-${colIndex}`}
                      value={cell}
                      placeholder="a+bi"
                      onChange={event =>
                        setMatrixGrid(prev =>
                          prev.map((r, ri) =>
                            r.map((value, ci) => {
                              if (ri === rowIndex && ci === colIndex) return event.target.value;
                              return value;
                            }),
                          ),
                        )
                      }
                    />
                  )),
                )}
              </div>
              <div className="flex gap-2">
                <Input value={matrixText} onChange={event => setMatrixText(event.target.value)} placeholder="1,0;0,1" />
                <Button variant="outline" size="sm" onClick={parseTextIntoGrid}>
                  Parse
                </Button>
                <Button variant="outline" size="sm" onClick={applyCustomFromText}>
                  Apply text
                </Button>
                <Button variant="default" size="sm" onClick={applyCustomFromGrid} disabled={!gridValidation.valid}>
                  Apply
                </Button>
              </div>
              <p className={cn("text-xs", gridValidation.valid ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400")}>
                {gridValidation.valid ? "Unitary: valid gate." : gridValidation.error ?? "Matrix is invalid."}
              </p>
            </section>

            {showHistory ? (
              <section className="space-y-2">
                <h3 className="text-sm font-semibold tracking-wide text-muted-foreground">History</h3>
                <div className="max-h-40 space-y-1 overflow-auto rounded-md border border-border/70 p-2">
                  {history.map((entry, index) => (
                    <button
                      key={entry.id}
                      className={cn(
                        "w-full rounded px-2 py-1 text-left text-xs transition-colors",
                        index === historyIndex ? "bg-primary text-primary-foreground" : "hover:bg-muted",
                      )}
                      onClick={() => {
                        setHistoryIndex(index);
                        emitChange(entry.state, { source: "external", historyIndex: index });
                      }}
                    >
                      {index}. {entry.label}
                    </button>
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </aside>
      </CardContent>
    </Card>
  );
}
